/**
 * Routes for the SMB self-serve flow.
 *
 * PUBLIC by design and therefore not wrapped in PrivateRoute at the app level:
 * a prospect has no account until screen two, so requiring one to reach screen
 * one would make the flow unreachable by the only people it is for.
 *
 * The steps are ordered, and the guards below enforce that by redirecting
 * rather than rendering a half-populated screen. Deep-linking to the trial with
 * no quote in memory would otherwise show a plan recap with nothing in it.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { SelfServeProvider, useSelfServe } from './context';
import { useSelfServeConfig } from './config';
import NotOpenYet from './pages/not-open-yet';
import Qualify from './pages/qualify';
import Account from './pages/account';
import Trial from './pages/trial';
import Payment from './pages/payment';

/** Sends the visitor back to the first step they have not completed. */
const useHasQuote = (): boolean => {
  const { cardOnFile, quote, flexQuote } = useSelfServe();
  return cardOnFile ? flexQuote !== null : quote !== null;
};

const RequireQuote = ({ children }: { children: JSX.Element }) =>
  useHasQuote() ? children : <Navigate to="/selfserve/qualify" replace />;

const RequireAccount = ({ children }: { children: JSX.Element }) => {
  const { account } = useSelfServe();
  if (!useHasQuote()) return <Navigate to="/selfserve/qualify" replace />;
  return account ? children : <Navigate to="/selfserve/account" replace />;
};

const SelfServeFlow = ({ cardOnFile }: { cardOnFile: boolean }) => (
  <SelfServeProvider cardOnFile={cardOnFile}>
    <Routes>
      <Route path="/" element={<Navigate to="/selfserve/qualify" replace />} />
      <Route path="qualify" element={<Qualify />} />
      <Route
        path="account"
        element={
          <RequireQuote>
            <Account />
          </RequireQuote>
        }
      />
      <Route
        path="trial"
        element={
          <RequireAccount>
            <Trial />
          </RequireAccount>
        }
      />
      <Route
        path="payment"
        element={
          <RequireAccount>
            <Payment />
          </RequireAccount>
        }
      />
      <Route path="*" element={<Navigate to="/selfserve/qualify" replace />} />
    </Routes>
  </SelfServeProvider>
);

/**
 * The flow waits for the backend to say which payment model it runs (config.ts) before showing anything, so the first
 * screen already asks the right questions. On a failure it offers a retry rather than guessing a flow.
 */
const SelfServeRoutes = () => {
  const config = useSelfServeConfig();
  if (config.status === 'loading') {
    return (
      <Spin
        aria-label="Loading"
        style={{ display: 'block', margin: '96px auto' }}
      />
    );
  }
  if (config.status === 'error') {
    return (
      <Result
        status="warning"
        title="We couldn't load signup just now"
        subTitle="Please try again in a moment."
        extra={
          <Button type="primary" onClick={config.retry}>
            Try again
          </Button>
        }
      />
    );
  }
  // Closed: the backend has no signup to offer (card on file is off and the old down-payment flow is gone), so no
  // step of the flow renders and nothing can post to /signup.
  if (config.mode === 'closed') return <NotOpenYet />;
  return <SelfServeFlow cardOnFile={config.mode === 'cardOnFile'} />;
};

export default SelfServeRoutes;
